import React, { useState, useEffect, useRef } from 'react';
import '../styles/TutorApplication.css'; // Reuse the iOS styles

/**
 * AppealChatModal Component
 * 
 * Reusable iOS-style chat interface for Appeals.
 * 
 * @param {boolean} isOpen - Control modal visibility
 * @param {Function} onClose - Handler to close modal
 * @param {string} title - Title of the chat (e.g. Tutor Name)
 * @param {string} subtitle - Subtitle (e.g. Language)
 * @param {Array} messages - Array of message objects {role, message, at}
 * @param {Function} onSendMessage - Function to handle sending message (optimistic logic should be in parent or hook)
 * @param {boolean} isLoading - Loading state for the send button
 * @param {string} currentUserRole - 'admin' or 'user'. Determines which side is "me".
 */
export default function AppealChatModal({
  isOpen,
  onClose,
  title,
  subtitle,
  messages,
  onSendMessage,
  isLoading,
  currentUserRole = 'user', // Default to user side logic, pass 'admin' for dashboard
  children // ✨ Allow injecting content (like Reason Card)
}) {
  const [inputText, setInputText] = useState('');
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isOpen, messages]);

  if (!isOpen) return null;

  const formatMessageTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    // Force AM/PM format using en-US locale
    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit', 
      hour12: true 
    });

    // ✨ Hide year: "1/26" instead of "1/26/2026"
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric'
    });

    return isToday 
      ? timeStr
      : `${dateStr} ${timeStr}`;
  };

  const handleSend = () => {
    if (!inputText.trim()) return;
    onSendMessage(inputText);
    setInputText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="ios-modal-overlay" onClick={onClose}>
      <div className="ios-modal" onClick={(e) => e.stopPropagation()}>
        
        {/* Header */}
        <div className="ios-modal-header">
          <div className="ios-modal-title" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <span>{title}</span>
            {subtitle && <span style={{ fontSize: '0.75rem', opacity: 0.6, fontWeight: 400 }}>{subtitle}</span>}
          </div>
          <button 
            className="ios-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Content Area */}
        <div className="ios-modal-content" style={{ paddingBottom: 0 }}>
          
          {/* ✨ Injected Content (Reason Card) */}
          {children}

          {/* Chat Area */}
          <div className="ios-chat-area" style={{ flex: 1, minHeight: 0 }}>            {messages && messages.length > 0 ? (
              messages.map((msg, idx) => {
                const isMe = currentUserRole === 'admin' 
                  ? msg.role === 'admin' 
                  : msg.role !== 'admin';

                // ✨ Intelligent Time Display Logic
                const prevMsg = messages[idx - 1];
                const timeDiff = prevMsg ? new Date(msg.at) - new Date(prevMsg.at) : 0;
                // Show time if it's the first message OR gap > 5 minutes (300000ms)
                const showTime = !prevMsg || timeDiff > 5 * 60 * 1000;

                return (
                  <React.Fragment key={idx}>
                    {showTime && (
                      <div className="ios-chat-time">
                        {formatMessageTime(msg.at)}
                      </div>
                    )}
                    <div className={`ios-chat-row ${isMe ? 'row-user' : 'row-admin'}`}>
                      {!isMe && <div className="ios-chat-spacer" />}
                      <div className={`ios-bubble ${isMe ? 'bubble-user' : 'bubble-admin'}`}>
                        {msg.message}
                      </div>
                      {isMe && <div className="ios-chat-spacer" />}
                    </div>
                  </React.Fragment>
                );
              })
            ) : (
              <div className="ios-empty-state">
                <p>No messages yet.</p>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Footer: Input Bar */}
        <div className="ios-input-bar">
          <textarea
            className="ios-input-field"
            placeholder="Type a message..."
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            rows={1}
            onKeyDown={handleKeyDown}
          />
          <button 
            className="ios-send-btn"
            onClick={handleSend}
            disabled={!inputText.trim() || isLoading}
          >
            {isLoading ? (
               <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spinning">
                  <circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="12"/>
               </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="19" x2="12" y2="5"/>
                <polyline points="5 12 12 5 19 12"/>
              </svg>
            )}
          </button>
        </div>

      </div>
    </div>
  );
}
